import { BaseNumber, NormalizedNumber, promiseAll } from '@sparkdotfi/common-universal'
import { queryOptions } from '@tanstack/react-query'
import { erc20Abi } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { getChainConfigEntry } from '@/config/chain'
import { BackedByQueryResult, SavingsBackedByData } from '@/config/chain/types'
import { sparkVaultV2Abi } from '@/config/contracts-generated'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export function sparkVaultV2EthBackedByQueryOptionsFactory({
  chainId,
  wagmiConfig,
}: {
  chainId: number
  wagmiConfig: Config
}): BackedByQueryResult {
  return {
    queryOptions: queryOptions({
      queryKey: sparkVaultV2EthBackedByQueryKey(),
      queryFn: () => sparkVaultV2EthBackedByQueryFunction({ chainId, wagmiConfig }),
    }),
    groupUsdsDetails: false,
  }
}

export function sparkVaultV2EthBackedByQueryKey(): string[] {
  return ['spark-vault-v2-eth-backed-by']
}

export async function sparkVaultV2EthBackedByQueryFunction({
  chainId,
  wagmiConfig,
}: {
  chainId: number
  wagmiConfig: Config
}): Promise<SavingsBackedByData> {
  const { tokenDefinitionRepository } = getChainConfigEntry(chainId)
  const weth = tokenDefinitionRepository.findOneBySymbol(TokenSymbol('WETH'))
  const speth = tokenDefinitionRepository.findOneBySymbol(TokenSymbol('spETH'))
  const { wethBalance: _wethBalance, totalAssets: _totalAssets } = await promiseAll(
    {
      wethBalance: () =>
        readContract(wagmiConfig, {
          address: weth.address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [speth.address],
        }),
      totalAssets: () =>
        readContract(wagmiConfig, {
          address: speth.address,
          abi: sparkVaultV2Abi,
          functionName: 'totalAssets',
        }),
    },
    { concurrency: 'unbounded' },
  )

  const wethBalance = BaseNumber(_wethBalance).toNormalizedUnit(weth.decimals)
  const totalAssets = BaseNumber(_totalAssets).toNormalizedUnit(weth.decimals)

  return [
    {
      category: 'sparklend_eth',
      amount: NormalizedNumber.max(totalAssets.minus(wethBalance), NormalizedNumber.ZERO),
    },
    {
      category: 'idle',
      amount: wethBalance,
    },
  ]
}
